"use client";

import { useCallback, useEffect, useRef, useState } from "react";

interface UseDeepLinkModalOptions {
  /**
   * Build the URL (path + query + hash) the address bar should show while the
   * item `id` is open. The Flow Template gallery returns the current path with
   * `?template=id`; the agent-template popup returns the template's canonical
   * detail path.
   */
  getUrlForId: (id: string) => string;
  /**
   * Optional. Read the currently deep-linked id straight from the URL, returning
   * it only when it points at a still-valid item, else `null`. Provide it for
   * surfaces that can be opened *from* the URL (a shared `?template=` link): the
   * hook then opens the matching item once on mount and re-derives open/closed on
   * every browser back/forward. Omit it for surfaces whose deep links are real
   * SSR routes (the agent popup) — opening is always user-initiated there, so the
   * hook never reads the URL and Back simply closes.
   */
  getIdFromUrl?: () => string | null;
  /**
   * Optional. Build the URL to restore when a deep-linked open is closed. Such an
   * open has no history entry of our own to pop, so we rewrite the URL in place
   * (e.g. strip `?template=`) instead. Only consulted when `getIdFromUrl` is set.
   */
  getClosedUrl?: () => string;
}

/**
 * Drives a modal whose open/closed state is mirrored into browser history, so
 * the Back button closes it. Opening pushes a history entry carrying the item's
 * URL; closing pops that entry (or, for a deep-linked open we never pushed,
 * rewrites the URL in place). Extracted from the Flow Template gallery and the
 * agent-template popup, which both hand-rolled this pushState/popstate dance.
 *
 * Pass memoized callbacks (the URL builders close over `items` / `locale`) so the
 * popstate listener only re-subscribes when those inputs actually change.
 */
export function useDeepLinkModal({ getUrlForId, getIdFromUrl, getClosedUrl }: UseDeepLinkModalOptions) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  // Whether WE pushed a history entry for the current open. Deep-linked opens
  // (the id was already on the URL at mount / after a popstate) don't push one,
  // so closing them must rewrite the URL in place instead of popping.
  const pushedStateRef = useRef(false);
  const didInitRef = useRef(false);
  // True only between a close()-initiated history.back() and the popstate it
  // fires. Lets the popstate handler tell "the user closed the modal" apart from
  // "the user pressed Back": a close must stay closed even when the entry we pop
  // back onto still carries a deep-link param (e.g. the page was deep-linked to A,
  // the user opened B, then closed B) — which would otherwise reopen the modal.
  const closingRef = useRef(false);

  const open = useCallback(
    (id: string) => {
      setSelectedId(id);
      setIsOpen(true);
      pushedStateRef.current = true;
      window.history.pushState({ deepLinkModal: id }, "", getUrlForId(id));
    },
    [getUrlForId],
  );

  const close = useCallback(() => {
    setIsOpen(false);
    if (pushedStateRef.current) {
      pushedStateRef.current = false;
      closingRef.current = true;
      window.history.back();
    } else if (getClosedUrl) {
      // Deep-linked open (no pushed entry): drop the param without a new entry.
      window.history.replaceState(window.history.state, "", getClosedUrl());
    }
  }, [getClosedUrl]);

  // Mirror the modal to the URL: open the deep-linked item once on mount, and
  // re-derive on every browser back/forward. Surfaces without `getIdFromUrl`
  // never read the URL, so this collapses to "Back closes the modal".
  useEffect(() => {
    const syncFromUrl = () => {
      const id = getIdFromUrl?.() ?? null;
      if (id) {
        setSelectedId(id);
        setIsOpen(true);
        return true;
      }
      return false;
    };

    // Deep-link on load: run once — all data is already SSR'd, so the modal
    // opens instantly.
    if (!didInitRef.current) {
      didInitRef.current = true;
      syncFromUrl();
    }

    const handlePopState = () => {
      pushedStateRef.current = false;
      // Our own close()'s history.back() landed here: stay closed regardless of
      // what the restored URL holds. If it still carries a deep-link param (we
      // popped back onto an earlier deep-linked entry), strip it in place so the
      // modal doesn't immediately reopen and the URL ends up clean.
      if (closingRef.current) {
        closingRef.current = false;
        setIsOpen(false);
        if (getClosedUrl && getIdFromUrl?.()) {
          window.history.replaceState(window.history.state, "", getClosedUrl());
        }
        return;
      }
      if (!syncFromUrl()) setIsOpen(false);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [getIdFromUrl, getClosedUrl]);

  return { selectedId, isOpen, open, close };
}
